import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Star, Map } from 'lucide-react';
import { FaTelegram } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function HomePage() {
  const navigate = useNavigate();
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [matrixLayers, setMatrixLayers] = useState<JSX.Element[][]>(Array(4).fill([]));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const createMatrixColumn = useCallback((index: number, layerIndex: number) => {
    const katakana = 'アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン';
    const latin = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nums = '0123456789';
    const alphabet = katakana + latin + nums;
    
    const columnLength = Math.floor(window.innerHeight / 10) + 25;
    
    const column = Array(columnLength).fill(0).map(() => {
      return Math.random() > 0.5 ? alphabet.charAt(Math.floor(Math.random() * alphabet.length)) : ' ';
    }).join('\n');
    
    const baseDuration = 15.84375;
    const duration = baseDuration + (layerIndex * 2.640625);
    const delay = (-Math.random() * 7.8125) * 1.3;
    const columnCount = isMobile ? 52 : 105;
    const left = `${(index * (100/columnCount))}%`;
    
    return (
      <div
        key={`column-${layerIndex}-${index}-${Date.now()}`}
        className="matrix-column"
        style={{
          left,
          animation: `matrixRain ${duration}s ${delay}s linear infinite`
        }}
      >
        {column}
      </div>
    );
  }, [isMobile]);

  useEffect(() => {
    const initializeLayers = () => {
      const columnCount = isMobile ? 52 : 105;
      const newLayers = Array(4).fill(0).map((_, layerIndex) => 
        Array(columnCount).fill(0).map((_, columnIndex) => createMatrixColumn(columnIndex, layerIndex))
      );
      setMatrixLayers(newLayers);
    };

    initializeLayers();

    const intervals = Array(4).fill(0).map((_, layerIndex) => 
      setInterval(() => {
        const columnCount = isMobile ? 52 : 105;
        const randomColumn = Math.floor(Math.random() * columnCount);
        setMatrixLayers(prev => {
          const newLayers = [...prev];
          const newLayer = [...newLayers[layerIndex]];
          newLayer[randomColumn] = createMatrixColumn(randomColumn, layerIndex);
          newLayers[layerIndex] = newLayer;
          return newLayers;
        });
      }, 281.25 + layerIndex * 93.75)
    );

    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        initializeLayers();
      }
    };

    window.addEventListener('resize', handleResize);

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.nav-button')) {
        setIsMenuOpen(false);
        document.body.classList.remove('menu-open');
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      intervals.forEach(interval => clearInterval(interval));
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
      document.body.classList.remove('menu-open');
    };
  }, [createMatrixColumn, isMobile]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleNavigation = (url: string) => {
    if (url !== "#") {
      window.location.href = url;
    }
    setIsMenuOpen(false);
    document.body.classList.remove('menu-open');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.classList.toggle('menu-open');
  };

  return (
    <div className="relative min-h-screen bg-black text-white overflow-visible">
      <div className="matrix-rain">
        {matrixLayers.map((layer, i) => (
          <div key={`layer-${i}`} className={`matrix-layer matrix-layer-${i + 1}`}>
            {layer}
          </div>
        ))}
      </div>
      
      <div
        className="cursor-glow"
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
        }}
      />
      
      <div className="relative z-0">
        <div className="mobile-nav-wrapper relative z-[10010]">
          <nav className="w-full px-6 py-4 flex justify-between items-center bg-black bg-opacity-70 backdrop-blur-sm">
            <div 
              className={`nav-button flex-1 max-w-[280px] ${isMenuOpen ? 'active' : ''}`}
              onClick={toggleMenu}
            >
              <span className="inline text-sm md:text-base truncate text-white">Helping Others Escape The Matrix</span>
              <div className="dropdown-menu absolute z-[10011]">
                <a href="#" onClick={() => navigate('/get-started')}>Getting Started</a>
                <a href="#" onClick={() => navigate('/contact-us')}>Contact Us</a>
                <a href="#" onClick={() => navigate('/blog')}>Blog</a>
                <a href="#" onClick={() => navigate('/testimonials')}>Testimonials</a>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="https://x.com/EscapeMatrixAI" target="_blank" rel="noopener noreferrer" className="nav-link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-current">
                  <path d="M16.99 0H20.298L13.071 8.26L21.573 19.5H14.916L9.702 12.683L3.736 19.5H0.426L8.156 10.665L0 0H6.826L11.539 6.231L16.99 0ZM15.829 17.52H17.662L5.83 1.876H3.863L15.829 17.52Z" fill="currentColor"/>
                </svg>
              </a>
              <a href="https://t.me/AIEscapingtheMatrix" target="_blank" rel="noopener noreferrer" className="nav-link flex items-center gap-2">
                <FaTelegram size={18} /> Telegram
              </a>
              <a href="mailto:EscapingtheMatrixAI@proton.me" className="nav-link flex items-center gap-2">
                <Mail size={18} /> Email
              </a>
            </div>
            
            <button 
              onClick={() => navigate('/testimonials')}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-green-400/10 hover:bg-green-400/20 transition-colors duration-300"
            >
              <Star size={20} className="text-green-400" />
            </button>
          </nav>
        </div>

        <div className="page-wrapper">
          <main className="flex flex-col items-center justify-center min-h-screen px-4 md:px-6 text-center relative z-0">
            <div className="mt-24 md:mt-36 space-y-4 md:space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-wider glitch-text text-green-400 leading-tight">
                ESCAPE THE MATRIX
              </h1>
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-wider glitch-text text-green-400 leading-tight">
                BUILD WITH AI
              </h1>
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-wider glitch-text text-green-400 leading-tight">
                SCALE WITH AUTOMATION
              </h1>
            </div>
            
            <p className="text-lg sm:text-xl md:text-2xl text-white font-light tracking-wide mt-8 mb-12">
              Custom Tools, Web Apps & AI Built for You
            </p>
            
            <div className="flex flex-col md:flex-row gap-6 items-center mb-16">
              <button 
                onClick={() => navigate('/get-started')} 
                className="cta-button rounded-md text-lg"
              >
                Get Started
              </button>
            </div>

            <div className="max-w-4xl mx-auto px-4 space-y-16 mb-32">
              <p className="text-2xl sm:text-3xl md:text-4xl text-white font-light leading-relaxed">
                EscapingTheMatrix is your launchpad for digital freedom.
              </p>
              
              <div className="grid gap-16 md:gap-20 text-gray-300">
                <p className="text-lg sm:text-xl md:text-2xl leading-relaxed max-w-3xl mx-auto">
                  We help creators and entrepreneurs build custom websites, web apps, lead-gen tools, and AI chatbots — fast.
                </p>
                
                <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
                  Whether you want to automate your business, grow your brand, or launch something totally unique, 
                  we give you the tools and support to build smarter and escape the limits of outdated tech.
                </p>
                
                <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
                  At our core, we're designing the future of intelligent automation — crafting streamlined, 
                  powerful systems that not only look great but work hard behind the scenes.
                </p>
                
                <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
                  With EscapingTheMatrix, you're not just building a digital presence — you're building a scalable, 
                  automated ecosystem that frees up your time and multiplies your impact.
                </p>
              </div>
            </div>
          </main>

          <footer className="fixed bottom-0 w-full px-6 py-4 bg-black bg-opacity-70 backdrop-blur-sm">
            <div className="flex justify-between">
              <div className="flex space-x-6">
                <a href="https://x.com/EscapeMatrixAI" target="_blank" rel="noopener noreferrer" className="nav-link">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-current">
                    <path d="M16.99 0H20.298L13.071 8.26L21.573 19.5H14.916L9.702 12.683L3.736 19.5H0.426L8.156 10.665L0 0H6.826L11.539 6.231L16.99 0ZM15.829 17.52H17.662L5.83 1.876H3.863L15.829 17.52Z" fill="currentColor"/>
                  </svg>
                </a>
                <a href="https://t.me/AIEscapingtheMatrix" target="_blank" rel="noopener noreferrer" className="nav-link">
                  <FaTelegram size={18} />
                </a>
                <a href="mailto:EscapingtheMatrixAI@proton.me" className="nav-link">
                  <Mail size={18} />
                </a>
                <a href="https://sitemap.escapingthematrix.io/" target="_blank" rel="noopener noreferrer" className="nav-link">
                  <Map size={18} />
                </a>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default HomePage;